import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { isExtinguisher } from "../shared/utils";

const formatCylinder = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
};

const toCylinderMonth = (dateStr) => (dateStr ? dateStr.slice(0, 7) : "");

const CLIPBOARD_KEY = "sfp_asset_clipboard";

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

function IconBtn({ onClick, title, icon, hoverBg, hoverColor, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? hoverBg : "#f1f5f9",
        border: "none", borderRadius: "6px", padding: "5px 7px",
        cursor: "pointer", color: hovered ? hoverColor : "#94a3b8",
        display: "inline-flex", alignItems: "center",
        transition: "background 0.15s, color 0.15s",
      }}>
      {icon}
    </button>
  );
}

export function FlatAssetRegister({ siteId, scopeType, scopeId, scopeLabel, onCountChange }) {
  const [efsms, setEfsms] = useState([]);
  const [flatAssets, setFlatAssets] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Copy/paste state. The clipboard lives in sessionStorage rather than in
  // component state so it survives switching register tabs, drilling into
  // another site, and the remounts both of those cause.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [clipboard, setClipboard] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(CLIPBOARD_KEY) || "null");
    } catch {
      return null;
    }
  });
  const [pasting, setPasting] = useState(false);
  const [copyMsg, setCopyMsg] = useState("");

  // Duplicate-in-place: the ticked rows are copied as one block after
  // `dupAfter` (blank = end of register); the backend renumbers what follows.
  const [dupOpen, setDupOpen] = useState(false);
  const [dupAfter, setDupAfter] = useState("");
  const [duplicating, setDuplicating] = useState(false);

  // The row the new-asset form is opened under ("Insert asset below"), or null
  // for the ordinary add row at the bottom.
  const [insertAfter, setInsertAfter] = useState(null);

  // Add row state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addCategoryId, setAddCategoryId] = useState("");
  const [addTypeId, setAddTypeId] = useState("");
  const [addItemNumber, setAddItemNumber] = useState("");
  const [addFloor, setAddFloor] = useState("");
  const [addLocation, setAddLocation] = useState("");
  const [addCylinder, setAddCylinder] = useState("");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");
  const locationRef = useRef(null);

  const load = () => {
    axios.get(`${API}/site-efsms/?${scopeType}=${scopeId}`)
      .then((res) => {
        const loaded = scopeType === "site"
          ? res.data.filter((e) => !e.unit && !e.area)
          : res.data;
        setEfsms(loaded);
        onCountChange && onCountChange(loaded.length);
        const rows = [];
        loaded.forEach((efsm) => {
          (efsm.assets || []).forEach((asset) => {
            rows.push({ ...asset, category_name: efsm.category_name, efsm_category_id: efsm.category });
          });
        });
        rows.sort((a, b) => {
          if (a.item_number == null) return 1;
          if (b.item_number == null) return -1;
          return a.item_number - b.item_number;
        });
        setFlatAssets(rows);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    axios.get(`${API}/asset-categories/`).then((r) => setAllCategories(r.data)).catch(() => {});
    axios.get(`${API}/asset-types/`).then((r) => setAllTypes(r.data)).catch(() => {});
  }, [scopeId]);

  const handleCategoryChange = (catId) => {
    setAddCategoryId(catId); setAddTypeId(""); setAddCylinder("");
  };

  const filteredTypes = (catId) =>
    catId ? allTypes.filter((t) => t.category === parseInt(catId) && t.is_active !== false) : [];

  // Keyed on the asset TYPE, not the category — the extinguisher register also
  // holds fire blankets, which have no cylinder.
  const selectedTypeName = allTypes.find((t) => t.id === parseInt(addTypeId))?.name || "";
  const addIsExtinguisher = isExtinguisher(selectedTypeName);
  const showCylinder = flatAssets.some((a) => isExtinguisher(a.asset_type_name)) || addIsExtinguisher;

  const COL    = showCylinder ? "28px 50px 2fr 2fr 1.5fr 2fr 110px 160px" : "28px 50px 2fr 2fr 1.5fr 2fr 160px";
  const addCOL = showCylinder ? "28px 50px 2fr 2fr 1.5fr 2fr 110px 150px" : "28px 50px 2fr 2fr 1.5fr 2fr 150px";

  const maxNumber = flatAssets.reduce((m, a) => Math.max(m, a.item_number || 0), 0);
  // Where an insert lands: straight after its row, or at the end when that
  // row has no number.
  const insertAfterNumber = insertAfter ? (insertAfter.item_number ?? maxNumber) : null;

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allSelected = flatAssets.length > 0 && selectedIds.size === flatAssets.length;
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(flatAssets.map((a) => a.id)));

  const flash = (msg, ms = 2500) => {
    setCopyMsg(msg);
    setTimeout(() => setCopyMsg(""), ms);
  };

  const handleCopy = () => {
    const ids = flatAssets.filter((a) => selectedIds.has(a.id)).map((a) => a.id);
    if (!ids.length) return;
    const payload = { assetIds: ids, count: ids.length, sourceLabel: scopeLabel || "" };
    try { sessionStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload)); } catch {}
    setClipboard(payload);
    setSelectedIds(new Set());
    flash(
      `${ids.length} row${ids.length !== 1 ? "s" : ""} copied` +
        (scopeLabel ? ` from ${scopeLabel}` : "")
    );
  };

  const handlePaste = async () => {
    if (!clipboard || !clipboard.assetIds || !clipboard.assetIds.length) return;
    setPasting(true);
    try {
      const res = await axios.post(`${API}/assets/copy/`, {
        asset_ids: clipboard.assetIds,
        site: siteId,
        area: scopeType === "area" ? scopeId : null,
        unit: scopeType === "unit" ? scopeId : null,
      });
      try { sessionStorage.removeItem(CLIPBOARD_KEY); } catch {}
      setClipboard(null);
      const created = res.data?.created ?? 0;
      const noPhotos = res.data?.skipped_photos ?? 0;
      flash(
        `${created} row${created !== 1 ? "s" : ""} pasted` +
          (noPhotos ? ` — ${noPhotos} photo${noPhotos !== 1 ? "s" : ""} not copied` : ""),
        noPhotos ? 5000 : 2500
      );
      load();
    } catch {
      flash("Paste failed. Please try again.", 3000);
    }
    setPasting(false);
  };

  const handleDuplicate = async () => {
    const ids = flatAssets.filter((a) => selectedIds.has(a.id)).map((a) => a.id);
    if (!ids.length) return;
    const after = dupAfter.trim() ? parseInt(dupAfter, 10) : null;
    if (after !== null && (isNaN(after) || after < 0)) {
      flash("Enter an asset number, or leave it blank for the end.", 3000);
      return;
    }
    setDuplicating(true);
    try {
      const res = await axios.post(`${API}/assets/duplicate/`, { asset_ids: ids, after_number: after });
      const created = res.data?.created ?? 0;
      const noPhotos = res.data?.skipped_photos ?? 0;
      setSelectedIds(new Set());
      setDupOpen(false);
      setDupAfter("");
      flash(
        `${created} row${created !== 1 ? "s" : ""} duplicated` +
          (noPhotos ? ` — ${noPhotos} photo${noPhotos !== 1 ? "s" : ""} not copied` : ""),
        noPhotos ? 5000 : 2500
      );
      load();
    } catch (err) {
      flash(err.response?.data?.asset_ids || "Duplicate failed. Please try again.", 3000);
    }
    setDuplicating(false);
  };

  const startInsert = (asset) => {
    setInsertAfter(asset);
    setShowAddForm(false);
    setAddCategoryId(asset.efsm_category_id ? String(asset.efsm_category_id) : "");
    setAddTypeId(asset.asset_type ? String(asset.asset_type) : "");
    setAddFloor(asset.floor_level || "");
    setAddLocation("");
    setAddCylinder("");
    setAddItemNumber("");
    setAddError("");
  };

  const closeAddForm = () => {
    setInsertAfter(null);
    setShowAddForm(false);
    setAddError("");
  };

  const smallBtn = (enabled) => ({
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    padding: "4px 8px",
    borderRadius: "5px",
    border: `1px solid ${enabled ? colors.blue : colors.border}`,
    backgroundColor: enabled ? colors.blue : "#fff",
    color: enabled ? "#fff" : "#cbd5e1",
    cursor: enabled ? "pointer" : "default",
  });

  const handleAdd = async () => {
    if (!addCategoryId) { setAddError("Select a category."); return; }
    if (!addLocation.trim()) { setAddError("Location is required."); return; }
    setSaving(true); setAddError("");
    try {
      // An insert keeps its row's own EFSM when the category is unchanged.
      let efsm = insertAfter && insertAfter.efsm_category_id === parseInt(addCategoryId)
        ? { id: insertAfter.site_efsm }
        : efsms.find((e) => e.category === parseInt(addCategoryId));
      if (!efsm) {
        const payload = { site: siteId, category: parseInt(addCategoryId) };
        if (scopeType === "unit") payload.unit = scopeId;
        if (scopeType === "area") payload.area = scopeId;
        // Inherit the site's existing testing schedule for this category, if
        // one is already set — otherwise a section/unit-scoped EFSM would
        // silently default to no schedule at all, invisible to inspections.
        const siteEfsmsRes = await axios.get(`${API}/site-efsms/?site=${siteId}`);
        const siteLevelSibling = siteEfsmsRes.data.find(
          (e) => e.category === parseInt(addCategoryId) && !e.unit && !e.area
        );
        if (siteLevelSibling) {
          payload.is_monthly = siteLevelSibling.is_monthly;
          payload.is_bi_annual = siteLevelSibling.is_bi_annual;
          payload.is_annual = siteLevelSibling.is_annual;
        }
        const r = await axios.post(`${API}/site-efsms/`, payload);
        efsm = r.data;
      }
      const assetPayload = {
        site_efsm: efsm.id,
        asset_type: addTypeId ? parseInt(addTypeId) : null,
        item_number: addItemNumber ? parseInt(addItemNumber) : null,
        floor_level: addFloor,
        location: addLocation,
      };
      if (addIsExtinguisher && addCylinder) assetPayload.cylinder_date = addCylinder + "-01";
      if (insertAfter) assetPayload.insert_after_number = insertAfterNumber;
      await axios.post(`${API}/assets/`, assetPayload);
      setAddItemNumber(""); setAddFloor(""); setAddLocation(""); setAddCylinder("");
      load();
      if (insertAfter) setInsertAfter(null);
      else locationRef.current?.focus();
    } catch { setAddError("Save failed."); }
    setSaving(false);
  };

  const startEdit = (asset) => {
    setEditingId(asset.id);
    setEditForm({
      item_number: asset.item_number ?? "",
      asset_type: asset.asset_type ?? "",
      floor_level: asset.floor_level ?? "",
      location: asset.location ?? "",
      cylinder_date: toCylinderMonth(asset.cylinder_date),
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async (asset) => {
    setEditSaving(true);
    try {
      const patch = {
        asset_type: editForm.asset_type ? parseInt(editForm.asset_type) : null,
        item_number: editForm.item_number ? parseInt(editForm.item_number) : null,
        floor_level: editForm.floor_level,
        location: editForm.location,
      };
      if (isExtinguisher(asset.asset_type_name)) {
        patch.cylinder_date = editForm.cylinder_date ? editForm.cylinder_date + "-01" : null;
      }
      await axios.patch(`${API}/assets/${asset.id}/`, patch);
      setEditingId(null);
      load();
    } catch {}
    setEditSaving(false);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm("Remove this asset?")) return;
    try {
      await axios.delete(`${API}/assets/${assetId}/`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Delete failed.");
    }
  };

  // The new-asset form, shared by the bottom add row and "Insert asset below".
  const renderAddForm = () => (
      <div style={{ backgroundColor: "#f0faf0", borderTop: `2px solid ${colors.green}` }}>
        <div style={{ padding: "5px 16px 2px", display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "11px", fontWeight: "700", color: colors.green, textTransform: "uppercase" }}>
            {insertAfter ? `+ Insert as #${insertAfterNumber + 1}` : "+ New Asset"}
          </span>
          <span onClick={closeAddForm} style={{ fontSize: "11px", color: "#94a3b8", cursor: "pointer" }}>
            Cancel
          </span>
          {addError && <span style={{ fontSize: "11px", color: colors.red }}>{addError}</span>}
          {saving && <span style={{ fontSize: "11px", color: colors.blue }}>Saving...</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: addCOL, padding: "5px 16px 10px", gap: "6px", alignItems: "center" }}>
          <div />
          {insertAfter ? (
            <div style={{ fontSize: "13px", color: "#64748b", textAlign: "center" }}>{insertAfterNumber + 1}</div>
          ) : (
            <input type="number" min="1" placeholder="#" value={addItemNumber}
              onChange={(e) => setAddItemNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px", textAlign: "center" }} />
          )}
          <select value={addCategoryId} onChange={(e) => handleCategoryChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px" }}>
            <option value="">— Category —</option>
            {allCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={addTypeId} onChange={(e) => setAddTypeId(e.target.value)}
            disabled={!addCategoryId}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px", opacity: addCategoryId ? 1 : 0.5 }}>
            <option value="">— Type —</option>
            {filteredTypes(addCategoryId).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input placeholder="Floor / Group" value={addFloor}
            onChange={(e) => setAddFloor(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px" }} />
          <input placeholder="Location *" value={addLocation}
            onChange={(e) => setAddLocation(e.target.value)}
            onKeyDown={(e) => !addIsExtinguisher && e.key === "Enter" && handleAdd()}
            ref={locationRef}
            autoFocus={!!insertAfter}
            style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px" }} />
          {showCylinder && (addIsExtinguisher
            ? <input type="month" value={addCylinder}
                onChange={(e) => setAddCylinder(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px" }} />
            : <div />
          )}
          <button onClick={handleAdd} disabled={saving}
            style={{
              ...styles.addButton,
              padding: "6px 12px",
              fontSize: "12px",
              backgroundColor: "#5a9c78",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}>
            Add
          </button>
        </div>
      </div>
  );

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={{
        display: "grid", gridTemplateColumns: COL, padding: "8px 16px",
        fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase",
        letterSpacing: "0.4px", borderBottom: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            disabled={flatAssets.length === 0}
            title={allSelected ? "Clear selection" : "Select all rows"}
            style={{ cursor: flatAssets.length ? "pointer" : "default", margin: 0 }}
          />
        </div>
        <div>#</div><div>Category</div><div>Type</div>
        <div>Floor / Group</div><div>Location</div>
        {showCylinder && <div>Cyl. Date</div>}
        <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
          <button
            onClick={() => setDupOpen(true)}
            disabled={selectedIds.size === 0}
            title={selectedIds.size ? `Duplicate ${selectedIds.size} selected row(s)` : "Tick rows to duplicate them"}
            style={smallBtn(selectedIds.size > 0)}>
            Duplicate{selectedIds.size ? ` (${selectedIds.size})` : ""}
          </button>
          <button
            onClick={handleCopy}
            disabled={selectedIds.size === 0}
            title={selectedIds.size ? `Copy ${selectedIds.size} selected row(s)` : "Tick rows to copy them"}
            style={smallBtn(selectedIds.size > 0)}>
            Copy{selectedIds.size ? ` (${selectedIds.size})` : ""}
          </button>
          <button
            onClick={handlePaste}
            disabled={!clipboard || pasting}
            title={clipboard
              ? `Paste ${clipboard.count} row(s)` +
                (clipboard.sourceLabel ? ` copied from ${clipboard.sourceLabel}` : " copied") +
                (scopeLabel ? ` into ${scopeLabel}` : " into this register")
              : "Nothing copied yet"}
            style={smallBtn(!!clipboard && !pasting)}>
            {pasting ? "..." : `Paste${clipboard ? ` (${clipboard.count})` : ""}`}
          </button>
        </div>
      </div>

      {dupOpen && selectedIds.size > 0 && (
        <div style={{
          padding: "6px 16px", fontSize: "12px", fontWeight: "600",
          color: colors.blue, backgroundColor: "#eff6ff",
          borderBottom: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap",
        }}>
          <span>
            Duplicate {selectedIds.size} asset{selectedIds.size !== 1 ? "s" : ""} — place after #
          </span>
          <input type="number" min="0" autoFocus value={dupAfter} placeholder="end"
            onChange={(e) => setDupAfter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleDuplicate();
              if (e.key === "Escape") setDupOpen(false);
            }}
            style={{ ...inputStyle, width: "70px", fontSize: "12px", padding: "3px 6px", textAlign: "center" }} />
          <span style={{ color: "#64748b", fontWeight: "400" }}>leave blank for the end of the register</span>
          <button onClick={handleDuplicate} disabled={duplicating} style={smallBtn(!duplicating)}>
            {duplicating ? "..." : "Duplicate"}
          </button>
          <span onClick={() => setDupOpen(false)} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: "400" }}>
            Cancel
          </span>
        </div>
      )}

      {(copyMsg || clipboard) && (
        <div style={{
          padding: "6px 16px", fontSize: "12px", fontWeight: "600",
          color: colors.blue, backgroundColor: "#eff6ff",
          borderBottom: `1px solid ${colors.border}`,
        }}>
          {copyMsg || (
            <>
              {clipboard.count} row{clipboard.count !== 1 ? "s" : ""} on the clipboard
              {clipboard.sourceLabel ? ` from ${clipboard.sourceLabel}` : ""} — press Paste to
              add {clipboard.count !== 1 ? "them" : "it"} here.
            </>
          )}
        </div>
      )}

      {/* Asset rows */}
      {flatAssets.length === 0 ? (
        <div style={{ padding: "16px", color: "#94a3b8", fontSize: "14px" }}>
          No assets yet. Use the row below to add the first one.
        </div>
      ) : flatAssets.map((asset, idx) => {
        const editing = asset.id === editingId;
        const rowTypes = filteredTypes(asset.efsm_category_id);
        const rowIsExt = isExtinguisher(asset.asset_type_name);

        const onEditKeyDown = (e) => {
          if (e.key === "Enter") saveEdit(asset);
          if (e.key === "Escape") cancelEdit();
        };

        return (
          <React.Fragment key={asset.id}>
          {editing ? (
          <div style={{
            display: "grid", gridTemplateColumns: COL,
            padding: "6px 16px", alignItems: "center",
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: "#fffbeb",
          }}>
            <div />
            <input type="number" min="1" autoFocus value={editForm.item_number}
              onChange={(e) => setEditForm({ ...editForm, item_number: e.target.value })}
              onKeyDown={onEditKeyDown}
              style={{ ...inputStyle, fontSize: "13px", padding: "3px 5px", textAlign: "center" }} />
            <div style={{ fontSize: "13px", color: "#64748b", padding: "0 4px" }}>{asset.category_name}</div>
            <select value={editForm.asset_type}
              onChange={(e) => setEditForm({ ...editForm, asset_type: e.target.value })}
              onKeyDown={onEditKeyDown}
              style={{ ...inputStyle, fontSize: "13px", padding: "3px 5px" }}>
              <option value="">— Type —</option>
              {rowTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <input value={editForm.floor_level}
              onChange={(e) => setEditForm({ ...editForm, floor_level: e.target.value })}
              onKeyDown={onEditKeyDown}
              style={{ ...inputStyle, fontSize: "13px", padding: "3px 5px" }} />
            <input value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              onKeyDown={onEditKeyDown}
              style={{ ...inputStyle, fontSize: "13px", padding: "3px 5px" }} />
            {showCylinder && (rowIsExt
              ? <input type="month" value={editForm.cylinder_date}
                  onChange={(e) => setEditForm({ ...editForm, cylinder_date: e.target.value })}
                  onKeyDown={onEditKeyDown}
                  style={{ ...inputStyle, fontSize: "13px", padding: "3px 5px" }} />
              : <div />
            )}
            <div style={{ display: "flex", gap: "4px" }}>
              <IconBtn onClick={() => saveEdit(asset)} title="Save (or press Enter)" icon={<CheckIcon />}
                hoverBg="#dcfce7" hoverColor="#16a34a" disabled={editSaving} />
              <IconBtn onClick={cancelEdit} title="Cancel (or press Esc)" icon={<XIcon />}
                hoverBg="#f1f5f9" hoverColor="#64748b" />
            </div>
          </div>
        ) : (
          <div
            onClick={() => startEdit(asset)}
            title="Click to edit"
            style={{
              display: "grid", gridTemplateColumns: COL,
              padding: "8px 16px", alignItems: "center",
              borderBottom: `1px solid ${colors.border}`,
              backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
              fontSize: "14px", cursor: "pointer",
            }}>
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={selectedIds.has(asset.id)}
                onChange={() => toggleRow(asset.id)}
                title="Select this row"
                style={{ cursor: "pointer", margin: 0 }}
              />
            </div>
            <div style={{ color: "#94a3b8", fontSize: "12px" }}>{asset.item_number || "—"}</div>
            <div style={{ color: colors.text }}>{asset.category_name}</div>
            <div style={{ color: colors.text }}>{asset.asset_type_name || "—"}</div>
            <div style={{ color: colors.text }}>{asset.floor_level || "—"}</div>
            <div style={{ color: colors.text }}>{asset.location}</div>
            {showCylinder && (
              <div style={{ color: rowIsExt ? colors.text : "#ccc" }}>
                {rowIsExt ? (formatCylinder(asset.cylinder_date) || "—") : "—"}
              </div>
            )}
            <div style={{ display: "flex", gap: "4px" }} onClick={(e) => e.stopPropagation()}>
              <IconBtn onClick={() => startEdit(asset)} title="Edit" icon={<EditIcon />}
                hoverBg="#dbeafe" hoverColor={colors.blue} />
              <IconBtn onClick={() => startInsert(asset)} title="Insert asset below" icon={<PlusIcon />}
                hoverBg="#f1f5f9" hoverColor={colors.text} />
              <IconBtn onClick={() => handleDelete(asset.id)} title="Delete" icon={<TrashIcon />}
                hoverBg="#f1f5f9" hoverColor={colors.text} />
            </div>
          </div>
          )}
          {insertAfter?.id === asset.id && renderAddForm()}
          </React.Fragment>
        );
      })}

      {/* Add row */}
      {insertAfter || !showAddForm ? (
        <div style={{ padding: "10px 16px" }}>
          <button
            onClick={() => { setInsertAfter(null); setShowAddForm(true); }}
            style={{
              ...styles.addButton,
              padding: "8px 16px",
              fontSize: "13px",
              backgroundColor: "#5a9c78",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}>
            + Add Asset
          </button>
        </div>
      ) : (
      renderAddForm()
      )}

      <div style={styles.count}>
        {flatAssets.length} asset{flatAssets.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}
